import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Loader2, Files, Send, Lock, UserCheck } from "lucide-react";
import { fmtDate, pretty } from "../../pages/external/referralConstants";

const STATUS_TONE = {
  requested: "text-[#B7791F] bg-[#B7791F]/12", uploaded: "text-[#2563EB] bg-[#2563EB]/12",
  under_review: "text-[#2C6480] bg-[#2C6480]/12", needs_clarification: "text-[#9C1E22] bg-[#9C1E22]/12",
  approved: "text-[#2F7D5A] bg-[#2F7D5A]/12", archived: "text-[#6B7280] bg-[#6B7280]/12",
};
const REVIEW_STATUSES = [["under_review", "Under Review"], ["needs_clarification", "Needs Clarification"], ["approved", "Approved"], ["archived", "Archived"]];

export default function StaffRefDocuments({ referralId }) {
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqType, setReqType] = useState("estimate");
  const [reqTitle, setReqTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null); // doc under review
  const [rStatus, setRStatus] = useState("");
  const [rInternal, setRInternal] = useState("");
  const [rExternal, setRExternal] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ext-referrals/${referralId}/documents`);
      setDocs(data.documents || []); setDocTypes(data.doc_types || {});
    } catch { /* silent */ } finally { setLoading(false); }
  }, [referralId]);
  useEffect(() => { load(); }, [load]);

  const request = async () => {
    setBusy(true);
    try {
      await api.post(`/ext-referrals/${referralId}/documents/request`, { doc_type: reqType, title: reqTitle || undefined });
      toast.success("Document requested from the partner.");
      setReqOpen(false); setReqTitle(""); await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const staffUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("doc_type", "other"); fd.append("external_visible", "true");
      await api.post(`/ext-referrals/${referralId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded & shared with the partner."); if (fileRef.current) fileRef.current.value = ""; await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const dl = async (d) => {
    try { await downloadFile(`/ext-documents/${d.id}/download`, d.filename || d.title); }
    catch (e) { toast.error(errMsg(e, "Download failed.")); }
  };

  const openReview = (d) => { setReview(d); setRStatus(d.status); setRInternal(d.staff_review_note || ""); setRExternal(""); };
  const saveReview = async () => {
    setBusy(true);
    try {
      await api.put(`/ext-documents/${review.id}`, {
        status: rStatus, staff_review_note: rInternal || undefined, external_response: rExternal || undefined,
      });
      toast.success("Review saved."); setReview(null); await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const toggleClientShare = async (d) => {
    try { await api.put(`/ext-documents/${d.id}`, { share_with_client: !d.client_shared }); await load();
      toast.success(!d.client_shared ? "Shared with client portal." : "Removed from client portal."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="p-5 border-[#E6EDF2]" data-testid="ext-ref-documents">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[13px] font-semibold text-[#252221] flex items-center gap-1.5"><Files size={15} /> Documents</div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => staffUpload(e.target.files?.[0])} data-testid="ext-doc-staff-file" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="border-[#D6E0E7] text-[#1C4358] h-8 text-[12px]">Upload</Button>
          <Button size="sm" onClick={() => setReqOpen(true)} className="bg-[#1C4358] hover:bg-[#153341] text-white h-8 text-[12px] gap-1" data-testid="ext-doc-request-btn"><Send size={13} /> Request</Button>
        </div>
      </div>

      {loading ? <div className="text-[13px] text-muted-foreground">Loading…</div> :
        docs.length === 0 ? <div className="text-[13px] text-muted-foreground">No documents yet. Request a quote/estimate from the partner.</div> : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="rounded-lg border border-[#E6EDF2] p-2.5" data-testid={`ext-doc-${d.id}`}>
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><FileText size={15} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium text-[#252221] truncate">{d.title}</div>
                    <div className="text-[11px] text-[#9AA5B1]">{d.doc_type_label} · {d.uploaded_by_kind ? (d.uploaded_by_kind === "external" ? "Partner" : "Staff") : "—"} · {fmtDate(d.updated_at)}</div>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[d.status] || STATUS_TONE.uploaded}`}>{d.status_label}</span>
                  {d.has_file && <button onClick={() => dl(d)} className="text-[#1C4358] hover:text-[#9C1E22]" title="Download" data-testid={`ext-doc-download-${d.id}`}><Download size={16} /></button>}
                </div>
                {d.staff_review_note && <div className="text-[12px] text-[#9C1E22] mt-1.5 flex items-start gap-1"><Lock size={11} className="mt-0.5" /> {d.staff_review_note}</div>}
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => openReview(d)} className="h-7 text-[11px] border-[#D6E0E7]" data-testid={`ext-doc-review-${d.id}`}>Review</Button>
                  {d.has_file && (
                    <Button size="sm" variant="outline" onClick={() => toggleClientShare(d)}
                      className={`h-7 text-[11px] gap-1 ${d.client_shared ? "border-[#2F7D5A]/40 text-[#2F7D5A]" : "border-[#D6E0E7] text-[#4B5563]"}`} data-testid={`ext-doc-share-${d.id}`}>
                      <UserCheck size={12} /> {d.client_shared ? "Shared w/ client" : "Share w/ client"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Request dialog */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent data-testid="ext-doc-request-dialog">
          <DialogHeader>
            <DialogTitle>Request a document</DialogTitle>
            <DialogDescription>Ask the partner to upload a specific document. They'll be notified in their portal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Select value={reqType} onValueChange={setReqType}>
              <SelectTrigger className="h-10" data-testid="ext-doc-request-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(docTypes).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={reqTitle} onChange={(e) => setReqTitle(e.target.value)} placeholder="Instructions / title (optional)" className="h-10" data-testid="ext-doc-request-title" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Cancel</Button>
            <Button onClick={request} disabled={busy} className="bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="ext-doc-request-submit">{busy && <Loader2 size={14} className="animate-spin" />} Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent data-testid="ext-doc-review-dialog">
          <DialogHeader>
            <DialogTitle>Review: {review?.title}</DialogTitle>
            <DialogDescription>Set a review status, add an internal note, and optionally reply to the partner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <label className="text-[12px] text-[#4B5563]">Review status</label>
              <Select value={rStatus} onValueChange={setRStatus}>
                <SelectTrigger className="h-10 mt-1" data-testid="ext-doc-review-status"><SelectValue /></SelectTrigger>
                <SelectContent>{REVIEW_STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-[12px] text-[#9C1E22]">Internal note (staff only)</label>
              <Textarea value={rInternal} onChange={(e) => setRInternal(e.target.value)} rows={2} className="text-[14px]" data-testid="ext-doc-review-internal" /></div>
            <div><label className="text-[12px] text-[#2F7D5A]">Response to partner (external-visible)</label>
              <Textarea value={rExternal} onChange={(e) => setRExternal(e.target.value)} rows={2} className="text-[14px]" placeholder="e.g. Thanks — can you clarify the ramp length?" data-testid="ext-doc-review-external" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)}>Cancel</Button>
            <Button onClick={saveReview} disabled={busy} className="bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="ext-doc-review-save">{busy && <Loader2 size={14} className="animate-spin" />} Save review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
